import React, { useEffect, useState } from "react";
import firebase from "firebase";
import styled from "styled-components";
import { completeMetaAuth } from "../../../../database/community/metaConnection";

// Landing screen for the Meta OAuth redirect (REDIRECT_PATH =
// /auth/meta/callback). The app runs on a HashRouter, so this real path is
// intercepted by pathname in App — react-router never sees it. Meta opens this
// in a popup; we read ?code/?state, call metaCompleteAuth (which carries the
// admin's Firebase auth via shared LOCAL persistence), then close the popup so
// the Media tab's focus listener refreshes the connection.

const Screen = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111111;
  color: #ffffff;
  font-family: var(--font-sans, system-ui, sans-serif);
  padding: 24px;
`;

const Card = styled.div`
  max-width: 380px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const Body = styled.div`
  font-size: 14px;
  color: #aaaaaa;
  line-height: 1.5;
`;

type Phase = "working" | "done" | "error";

const MetaCallback: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("working");
  const [message, setMessage] = useState("Finishing your connection…");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code") || "";
    const state = params.get("state") || "";
    const oauthError =
      params.get("error_message") ||
      params.get("error") ||
      params.get("error_description");

    if (oauthError) {
      setPhase("error");
      setMessage(`Meta returned an error: ${oauthError}`);
      return;
    }
    if (!code || !state) {
      setPhase("error");
      setMessage("Missing code or state from Meta. Try connecting again.");
      return;
    }

    let handled = false;
    const unsub = firebase.auth().onAuthStateChanged(async (user) => {
      if (handled) return;
      if (!user) return;
      handled = true;
      unsub();
      try {
        const res = await completeMetaAuth({ code, state });
        setPhase("done");
        setMessage(
          res.connected
            ? "Instagram / Facebook connected. You can close this window."
            : "Connection finished. You can close this window."
        );
        setTimeout(() => {
          if (window.opener) window.close();
        }, 1200);
      } catch (e: any) {
        setPhase("error");
        setMessage(e?.message || "Could not finish the connection.");
      }
    });

    return () => unsub();
  }, []);

  return (
    <Screen>
      <Card>
        <Title>
          {phase === "working"
            ? "Connecting…"
            : phase === "done"
            ? "All set"
            : "Something went wrong"}
        </Title>
        <Body>{message}</Body>
        {phase !== "working" ? (
          <Body>
            <a href="/" style={{ color: "#fff" }}>
              Back to the admin
            </a>
          </Body>
        ) : null}
      </Card>
    </Screen>
  );
};

export default MetaCallback;
